import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import { FiAlertTriangle, FiClock, FiList, FiRefreshCw, FiSend } from 'react-icons/fi';
import CommonLoader from '../../../components/common/CommonLoader';
import STPStatusBadge from './STPStatusBadge';
import '../styles/STPSubmissionPanel.css';
import { GRAPHQL_HTTP_URL } from '../../../config/api';

const GQL = GRAPHQL_HTTP_URL;

const GET_STATUS = `
  query GetPayrollSTPStatus($payrollId: String!) {
    getPayrollSTPStatus(payrollId: $payrollId) {
      payrollId
      stpStatus
      lastSubmissionDate
      submissionType
      errorMessage
    }
  }
`;

const GET_SUBMISSIONS = `
  query GetSTPSubmissions($payrollId: String!) {
    getSTPSubmissions(payrollId: $payrollId) {
      id
      createdAt
      submittedAt
      submissionType
      status
      atoReceiptId
      atoStatusMessage
      validationErrors
      retryCount
    }
  }
`;

const SUBMIT = `
  mutation SubmitSTP($payrollId: String!) {
    submitSTP(payrollId: $payrollId) {
      success
      message
      submission { id status }
    }
  }
`;

const RESUBMIT = `
  mutation ResubmitSTP($submissionId: String!) {
    resubmitSTP(submissionId: $submissionId) {
      success
      message
      submission { id status retryCount }
    }
  }
`;

const gql = async (query, variables = {}) => {
  const token = Cookies.get('accessToken');
  const res = await fetch(GQL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data;
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

function parseValidationErrors(raw) {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export default function STPSubmissionPanel({ payrollId, payrollFinalized }) {
  const [status, setStatus] = useState(null);
  const [submissions, setSubmissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);
  const [error, setError] = useState('');
  const [resultMsg, setResultMsg] = useState('');

  const load = useCallback(async () => {
    if (!payrollId) return;
    setLoading(true);
    setError('');
    try {
      const [s, h] = await Promise.all([
        gql(GET_STATUS, { payrollId }),
        gql(GET_SUBMISSIONS, { payrollId }),
      ]);
      setStatus(s.getPayrollSTPStatus);
      setSubmissions(h.getSTPSubmissions ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [payrollId]);

  useEffect(() => { load(); }, [load]);

  const latest = useMemo(() => (submissions && submissions.length ? submissions[0] : null), [submissions]);
  const latestErrors = useMemo(() => parseValidationErrors(latest?.validationErrors), [latest]);

  const canSubmit = payrollFinalized && (status?.stpStatus === 'NOT_SUBMITTED');
  const canResubmit = payrollFinalized && (status?.stpStatus === 'FAILED') && latest;
  const canViewHistory = (status?.stpStatus && status?.stpStatus !== 'NOT_SUBMITTED') || (submissions?.length > 0);

  const handleSubmit = async () => {
    setActionLoading(true);
    setResultMsg('');
    setError('');
    try {
      const data = await gql(SUBMIT, { payrollId });
      setResultMsg(data.submitSTP?.message || 'STP submission queued');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetry = async (submissionId) => {
    setRetryingId(submissionId);
    setResultMsg('');
    setError('');
    try {
      const data = await gql(RESUBMIT, { submissionId });
      setResultMsg(data.resubmitSTP?.message || 'STP resubmission queued');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="stp-panel">
      <div className="stp-panel__header">
        <div>
          <div className="stp-panel__title">STP Submission</div>
          <div className="stp-panel__sub">
            Submit payroll data to the ATO and track submission status.
          </div>
        </div>
        <button className="stp-panel__btn stp-panel__btn--ghost" onClick={load} disabled={loading}>
          <FiRefreshCw className={loading ? 'stp-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="stp-panel__banner stp-panel__banner--error">
          <FiAlertTriangle /> {error}
        </div>
      )}
      {resultMsg && (
        <div className="stp-panel__banner stp-panel__banner--ok">
          <FiClock /> {resultMsg}
        </div>
      )}

      {loading && !status ? (
        <div className="stp-panel__loader"><CommonLoader message="Loading STP status…" /></div>
      ) : (
        <div className="stp-panel__grid">
          <div className="stp-panel__card">
            <div className="stp-panel__k">STP Status</div>
            <div className="stp-panel__v">
              <STPStatusBadge status={status?.stpStatus || 'NOT_SUBMITTED'} />
            </div>
          </div>
          <div className="stp-panel__card">
            <div className="stp-panel__k">Last submission date</div>
            <div className="stp-panel__v">{fmtDateTime(status?.lastSubmissionDate)}</div>
          </div>
          <div className="stp-panel__card">
            <div className="stp-panel__k">Submission type</div>
            <div className="stp-panel__v">{status?.submissionType || '—'}</div>
          </div>
          <div className="stp-panel__card">
            <div className="stp-panel__k">Error message</div>
            <div className="stp-panel__v stp-panel__v--mono">{status?.errorMessage || '—'}</div>
          </div>
        </div>
      )}

      <div className="stp-panel__actions">
        {canSubmit && (
          <button className="stp-panel__btn stp-panel__btn--primary" onClick={handleSubmit} disabled={actionLoading}>
            <FiSend />
            {actionLoading ? 'Submitting…' : 'Submit STP'}
          </button>
        )}
        {canResubmit && (
          <button
            className="stp-panel__btn stp-panel__btn--primary"
            onClick={() => handleRetry(latest.id)}
            disabled={retryingId === latest.id}
          >
            <FiSend />
            {retryingId === latest.id ? 'Resubmitting…' : 'Resubmit STP'}
          </button>
        )}
        {canViewHistory && (
          <a className="stp-panel__btn stp-panel__btn--secondary" href="#stp-history">
            <FiList />
            View Submission History
          </a>
        )}

        {!payrollFinalized && (
          <div className="stp-panel__hint">
            Payroll must be finalized/locked before STP submission is allowed.
          </div>
        )}
      </div>

      <div id="stp-history" className="stp-panel__history">
        <div className="stp-panel__history-title">Submission History</div>

        {loading && submissions === null ? (
          <div className="stp-panel__loader"><CommonLoader message="Loading submissions…" /></div>
        ) : submissions?.length ? (
          <div className="stp-panel__table-wrap">
            <table className="stp-panel__table">
              <thead>
                <tr>
                  <th>Submission Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => {
                  const payrollStatus =
                    s.status === 'PENDING' ? 'PENDING'
                      : (s.status === 'SUCCESS' || s.status === 'WARNING') ? 'SUBMITTED'
                        : (s.status === 'FAILED' || s.status === 'REJECTED') ? 'FAILED'
                          : 'NOT_SUBMITTED';
                  const canRetry = payrollFinalized && payrollStatus === 'FAILED' && (s.retryCount ?? 0) < 3;
                  const valErrs = parseValidationErrors(s.validationErrors);
                  return (
                    <tr key={s.id}>
                      <td>{fmtDateTime(s.submittedAt || s.createdAt)}</td>
                      <td>{s.submissionType === 'UPDATEEVENT' ? 'Update Event' : 'Pay Event'}</td>
                      <td><STPStatusBadge status={payrollStatus} /></td>
                      <td className="stp-panel__msg">
                        {s.atoStatusMessage || (valErrs?.length ? `${valErrs.length} validation error(s)` : '—')}
                      </td>
                      <td>
                        <div className="stp-panel__row-actions">
                          {canRetry && (
                            <button
                              className="stp-panel__btn stp-panel__btn--small"
                              onClick={() => handleRetry(s.id)}
                              disabled={retryingId === s.id}
                              title={`Retry (${s.retryCount}/3 attempts used)`}
                            >
                              <FiRefreshCw className={retryingId === s.id ? 'stp-spin' : ''} />
                              Retry
                            </button>
                          )}
                          {valErrs?.length ? (
                            <details className="stp-panel__details">
                              <summary>View</summary>
                              <div className="stp-panel__errors">
                                {valErrs.map((e, idx) => (
                                  <div key={idx} className="stp-panel__error-item">
                                    <strong>{e.code || e.Code || 'ERROR'}</strong>
                                    {e.field || e.Location ? ` [${e.field || e.Location}]` : ''}: {e.message || e.Text || e.Description}
                                  </div>
                                ))}
                              </div>
                            </details>
                          ) : (
                            <span className="stp-panel__muted">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="stp-panel__empty">No STP submissions for this payroll yet.</div>
        )}

        {latestErrors?.length ? (
          <div className="stp-panel__why">
            <div className="stp-panel__why-title">Why submission failed</div>
            <div className="stp-panel__why-sub">
              Fix the validation errors below (e.g. missing TFN, missing employee details), then resubmit.
            </div>
            <div className="stp-panel__errors">
              {latestErrors.map((e, idx) => (
                <div key={idx} className="stp-panel__error-item">
                  <strong>{e.code || e.Code || 'ERROR'}</strong>
                  {e.field || e.Location ? ` [${e.field || e.Location}]` : ''}: {e.message || e.Text || e.Description}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

